import { useState, useEffect, useMemo, useRef } from 'react';
import {
  MapPin,
  Navigation,
  X,
  ChevronLeft,
  ChevronRight,
  Heart,
  Share2,
  Images,
  Wifi,
  Sun,
  Zap,
  ShieldCheck,
  Shirt,
  Bath,
  Bus,
  Waves,
  Dumbbell,
  Tv,
  Flame,
  Utensils,
  Droplets,
  Bed,
  Users,
  BadgeCheck,
  CalendarCheck,
  Wallet,
  MessageCircle,
  ExternalLink,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Helpers: read amenities, price and campuses from the existing data */
/*  (no changes needed to your accommodations data file)               */
/* ------------------------------------------------------------------ */

const AMENITIES = [
  { label: 'Unlimited Wi-Fi', icon: Wifi, test: /wi-?fi|fib(er|re)/i },
  { label: 'En-suite rooms', icon: Bath, test: /en.?suite/i },
  { label: '24-hour security', icon: ShieldCheck, test: /security|armed|surveillance|camera/i },
  { label: 'Solar backup', icon: Sun, test: /solar/i },
  { label: 'Backup generator', icon: Zap, test: /generator/i },
  { label: 'Laundry', icon: Shirt, test: /laundry|washing|tumble/i },
  { label: 'Transport to campus', icon: Bus, test: /transport|\bbus\b/i },
  { label: 'Swimming pool', icon: Waves, test: /pool/i },
  { label: 'Gym nearby', icon: Dumbbell, test: /gym/i },
  { label: 'TV lounge', icon: Tv, test: /\btv\b|dstv/i },
  { label: 'Braai / lapa area', icon: Flame, test: /braai|lapa/i },
  { label: 'Kitchen appliances', icon: Utensils, test: /stove|microwave|fridge|refrigerator|freezer/i },
  { label: 'Water backup', icon: Droplets, test: /jojo|water backup/i },
  { label: 'Single rooms', icon: Bed, test: /single/i },
  { label: 'Sharing rooms', icon: Users, test: /sharing|shared/i },
];

const HIGHLIGHTS = [
  { label: 'No deposit required', icon: Wallet, test: /no deposit/i },
  { label: '6-month stays accepted', icon: CalendarCheck, test: /6.?month/i },
];

const isNsfas = (text) => /n[sf]{2}as/i.test(text);

const getAmount = (price = '') => {
  const match = price.match(/R\s?[\d,\s]*\d/);
  return match ? match[0].replace(/\s/g, '') : null;
};

const getCampuses = (text) => {
  const campuses = [];
  if (/vut/i.test(text)) campuses.push('VUT');
  if (/nwu|north.?west/i.test(text)) campuses.push('NWU');
  if (/sedibeng/i.test(text)) campuses.push('Sedibeng');
  return campuses;
};

const FAV_KEY = 'v2v-favourites';
const readFavs = () => {
  try {
    return JSON.parse(localStorage.getItem(FAV_KEY)) || [];
  } catch {
    return [];
  }
};

const useSwipe = (onLeft, onRight) => {
  const startX = useRef(null);
  return {
    onTouchStart: (e) => {
      startX.current = e.touches[0].clientX;
    },
    onTouchEnd: (e) => {
      if (startX.current === null) return;
      const dx = e.changedTouches[0].clientX - startX.current;
      if (Math.abs(dx) > 40) (dx < 0 ? onLeft : onRight)();
      startX.current = null;
    },
  };
};

/* ------------------------------------------------------------------ */

const AccommodationCard = ({
  name,
  location,
  price,
  distance,
  image,
  images = [],
  paymentType = '',
  whatsappNumber = '27729792961', // default number
}) => {
  const allImages = useMemo(
    () => (images.length > 0 ? images : [image]).filter(Boolean),
    [images, image]
  );
  const n = allImages.length;

  const title = (name || '').trim();
  const address = (location || '').trim();
  const allText = `${price} ${distance} ${paymentType}`;

  const amenities = useMemo(() => AMENITIES.filter((a) => a.test.test(allText)), [allText]);
  const highlights = useMemo(() => HIGHLIGHTS.filter((h) => h.test.test(allText)), [allText]);
  const nsfas = isNsfas(allText);
  const amount = getAmount(price);
  const campuses = getCampuses(`${distance} ${price}`);

  const [cardIndex, setCardIndex] = useState(0);
  const [showDetails, setShowDetails] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(null); // null = closed
  const [isFav, setIsFav] = useState(() => readFavs().includes(title));
  const [shareMsg, setShareMsg] = useState('');
  const thumbRefs = useRef([]);

  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
    `Hi! I'm interested in ${title}. Can you provide more details?`
  )}`;

  const mapsLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    /vanderbijlpark/i.test(address) ? address : `${address}, Vanderbijlpark`
  )}`;

  const cardNext = () => setCardIndex((i) => (i + 1) % n);
  const cardPrev = () => setCardIndex((i) => (i - 1 + n) % n);
  const galleryNext = () => setGalleryIndex((i) => (i + 1) % n);
  const galleryPrev = () => setGalleryIndex((i) => (i - 1 + n) % n);

  const cardSwipe = useSwipe(cardNext, cardPrev);
  const gallerySwipe = useSwipe(galleryNext, galleryPrev);

  // Stop the page behind from scrolling while a popup is open
  useEffect(() => {
    if (!showDetails && galleryIndex === null) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showDetails, galleryIndex]);

  // Keyboard: Esc closes, arrow keys move through photos
  useEffect(() => {
    if (!showDetails && galleryIndex === null) return undefined;
    const onKey = (e) => {
      if (galleryIndex !== null) {
        if (e.key === 'Escape') setGalleryIndex(null);
        if (e.key === 'ArrowRight') setGalleryIndex((i) => (i + 1) % n);
        if (e.key === 'ArrowLeft') setGalleryIndex((i) => (i - 1 + n) % n);
      } else if (e.key === 'Escape') {
        setShowDetails(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showDetails, galleryIndex, n]);

  // Keep the active thumbnail visible in the gallery strip
  useEffect(() => {
    if (galleryIndex === null) return;
    thumbRefs.current[galleryIndex]?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }, [galleryIndex]);

  const toggleFav = (e) => {
    e.stopPropagation();
    const favs = readFavs();
    const next = favs.includes(title) ? favs.filter((f) => f !== title) : [...favs, title];
    try {
      localStorage.setItem(FAV_KEY, JSON.stringify(next));
    } catch {
      /* storage unavailable: keep it in memory only */
    }
    setIsFav(next.includes(title));
  };

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title, text: `${title}: ${address}`, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setShareMsg('Link copied');
      setTimeout(() => setShareMsg(''), 2000);
    } catch {
      /* user cancelled the share sheet */
    }
  };

  const priceLabel = amount ? (
    <>
      <span className="font-bold">{amount}</span>
      <span className="text-gray-500"> / month</span>
    </>
  ) : nsfas ? (
    <span className="font-bold">NSFAS funding accepted</span>
  ) : (
    <span className="font-bold">{price}</span>
  );

  // Airbnb-style dots: show at most 5 at a time
  const dotCount = Math.min(5, n);
  const dotStart = Math.min(Math.max(cardIndex - 2, 0), n - dotCount);

  const gridImages = n >= 5 ? allImages.slice(0, 5) : allImages.slice(0, 1);

  return (
    <>
      {/* ============================ CARD ============================ */}
      <article className="group flex flex-col">
        <div
          className="relative aspect-[4/3] cursor-pointer overflow-hidden rounded-2xl bg-gray-200"
          onClick={() => setShowDetails(true)}
          {...cardSwipe}
        >
          <img
            src={allImages[cardIndex]}
            alt={`${title}, photo ${cardIndex + 1}`}
            loading="lazy"
            className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
          />

          {nsfas && (
            <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-white/95 px-3 py-1 text-xs font-semibold text-gray-900 shadow-sm">
              <BadgeCheck className="h-3.5 w-3.5 text-amber-600" />
              NSFAS accredited
            </span>
          )}

          <button
            onClick={toggleFav}
            aria-label={isFav ? 'Remove from saved' : 'Save this place'}
            className="absolute right-3 top-3 rounded-full p-1.5 transition hover:scale-110"
          >
            <Heart
              className={`h-6 w-6 drop-shadow ${
                isFav ? 'fill-yellow-400 text-yellow-400' : 'fill-black/30 text-white'
              }`}
            />
          </button>

          {n > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  cardPrev();
                }}
                aria-label="Previous photo"
                className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-1.5 opacity-0 shadow transition hover:scale-105 group-hover:opacity-100"
              >
                <ChevronLeft className="h-4 w-4 text-gray-900" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  cardNext();
                }}
                aria-label="Next photo"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-1.5 opacity-0 shadow transition hover:scale-105 group-hover:opacity-100"
              >
                <ChevronRight className="h-4 w-4 text-gray-900" />
              </button>

              <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5">
                {Array.from({ length: dotCount }, (_, k) => {
                  const idx = dotStart + k;
                  return (
                    <span
                      key={idx}
                      className={`rounded-full bg-white transition-all ${
                        idx === cardIndex ? 'h-2 w-2 opacity-100' : 'h-1.5 w-1.5 opacity-60'
                      }`}
                    />
                  );
                })}
              </div>

              <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-full bg-black/50 px-2 py-0.5 text-xs font-medium text-white">
                <Images className="h-3 w-3" />
                {n}
              </span>
            </>
          )}
        </div>

        <div className="mt-3 flex flex-1 flex-col">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold leading-snug text-gray-900">{title}</h3>
            {campuses.length > 0 && (
              <div className="flex shrink-0 gap-1">
                {campuses.map((c) => (
                  <span
                    key={c}
                    className="rounded-full bg-yellow-100 px-2 py-0.5 text-[11px] font-semibold text-yellow-800"
                  >
                    {c}
                  </span>
                ))}
              </div>
            )}
          </div>

          <p className="mt-1 flex items-center gap-1.5 text-sm text-gray-500">
            <MapPin className="h-3.5 w-3.5 shrink-0" />
            {address}
          </p>
          <p className="mt-0.5 flex items-start gap-1.5 text-sm text-gray-500">
            <Navigation className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {distance}
          </p>

          {amenities.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {amenities.slice(0, 4).map(({ label, icon: Icon }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-700"
                >
                  <Icon className="h-3 w-3" />
                  {label}
                </span>
              ))}
              {amenities.length > 4 && (
                <span className="rounded-full px-1.5 py-1 text-xs text-gray-500">
                  +{amenities.length - 4} more
                </span>
              )}
            </div>
          )}

          <p className="mt-3 text-gray-900">{priceLabel}</p>

          <div className="mt-auto flex gap-2 pt-4">
            <button
              onClick={() => setShowDetails(true)}
              className="flex-1 rounded-xl bg-yellow-400 px-4 py-2.5 text-sm font-semibold text-gray-900 transition hover:bg-yellow-500"
            >
              View details
            </button>
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Enquire about ${title} on WhatsApp`}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-green-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-green-700"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp
            </a>
          </div>
        </div>
      </article>

      {/* ========================= LISTING PAGE ======================== */}
      {showDetails && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-white" role="dialog" aria-modal="true" aria-label={title}>
          <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-100 bg-white/95 px-4 py-3 backdrop-blur sm:px-8">
            <button
              onClick={() => setShowDetails(false)}
              className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium text-gray-900 hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
              Close
            </button>
            <div className="flex items-center gap-1">
              {shareMsg && <span className="mr-2 text-sm text-gray-500">{shareMsg}</span>}
              <button
                onClick={share}
                className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium text-gray-900 hover:bg-gray-100"
              >
                <Share2 className="h-4 w-4" />
                <span className="hidden sm:inline">Share</span>
              </button>
              <button
                onClick={toggleFav}
                className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium text-gray-900 hover:bg-gray-100"
              >
                <Heart className={`h-4 w-4 ${isFav ? 'fill-yellow-400 text-yellow-400' : ''}`} />
                <span className="hidden sm:inline">{isFav ? 'Saved' : 'Save'}</span>
              </button>
            </div>
          </div>

          <div className="mx-auto max-w-6xl px-4 pb-32 pt-6 sm:px-8 lg:pb-16">
            <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl">{title}</h2>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600">
              <a
                href={mapsLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 font-medium underline underline-offset-2 hover:text-gray-900"
              >
                <MapPin className="h-4 w-4" />
                {address}
              </a>
              {campuses.map((c) => (
                <span key={c} className="rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-semibold text-yellow-800">
                  Near {c}
                </span>
              ))}
            </div>

            {/* Photo grid */}
            <div
              className={`relative mt-6 grid h-[260px] gap-2 overflow-hidden rounded-2xl sm:h-[440px] ${
                gridImages.length === 5 ? 'sm:grid-cols-4 sm:grid-rows-2' : ''
              }`}
            >
              {gridImages.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setGalleryIndex(i)}
                  className={`relative overflow-hidden bg-gray-200 ${
                    i === 0 ? 'sm:col-span-2 sm:row-span-2' : 'hidden sm:block'
                  }`}
                >
                  <img
                    src={src}
                    alt={`${title}, photo ${i + 1}`}
                    className="h-full w-full object-cover transition duration-300 hover:brightness-90"
                  />
                </button>
              ))}
              <button
                onClick={() => setGalleryIndex(0)}
                className="absolute bottom-4 right-4 inline-flex items-center gap-2 rounded-lg border border-gray-900 bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-50"
              >
                <Images className="h-4 w-4" />
                Show all {n} photos
              </button>
            </div>

            <div className="mt-8 grid gap-10 lg:grid-cols-[1fr_360px]">
              <div>
                {/* Key facts */}
                <ul className="space-y-5">
                  <li className="flex gap-4">
                    <Navigation className="mt-0.5 h-6 w-6 shrink-0 text-gray-900" />
                    <div>
                      <p className="font-semibold text-gray-900">Distance to campus</p>
                      <p className="text-sm text-gray-600">{distance}</p>
                    </div>
                  </li>
                  {nsfas && (
                    <li className="flex gap-4">
                      <BadgeCheck className="mt-0.5 h-6 w-6 shrink-0 text-amber-600" />
                      <div>
                        <p className="font-semibold text-gray-900">NSFAS accredited</p>
                        <p className="text-sm text-gray-600">Accredited for NSFAS-funded students.</p>
                      </div>
                    </li>
                  )}
                  {highlights.map(({ label, icon: Icon }) => (
                    <li key={label} className="flex gap-4">
                      <Icon className="mt-0.5 h-6 w-6 shrink-0 text-gray-900" />
                      <p className="font-semibold text-gray-900">{label}</p>
                    </li>
                  ))}
                </ul>

                <hr className="my-8 border-gray-200" />

                <h3 className="text-xl font-semibold text-gray-900">About this place</h3>
                <p className="mt-3 whitespace-pre-line leading-relaxed text-gray-700">{paymentType}</p>

                <hr className="my-8 border-gray-200" />

                <h3 className="text-xl font-semibold text-gray-900">What this place offers</h3>
                {amenities.length > 0 ? (
                  <ul className="mt-4 grid gap-4 sm:grid-cols-2">
                    {amenities.map(({ label, icon: Icon }) => (
                      <li key={label} className="flex items-center gap-3 text-gray-700">
                        <Icon className="h-5 w-5 shrink-0 text-gray-900" />
                        {label}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-3 text-gray-600">
                    Message the residence on WhatsApp for the full list of amenities.
                  </p>
                )}

                <hr className="my-8 border-gray-200" />

                <h3 className="text-xl font-semibold text-gray-900">Where you'll be</h3>
                <p className="mt-2 text-gray-700">{address}</p>
                <a
                  href={mapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-gray-900 underline underline-offset-2"
                >
                  Open in Google Maps
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>

              {/* Booking box (desktop) */}
              <aside className="hidden lg:block">
                <div className="sticky top-24 rounded-2xl border border-gray-200 p-6 shadow-xl">
                  <p className="text-xl text-gray-900">{priceLabel}</p>
                  {nsfas && amount && (
                    <p className="mt-1 text-sm text-gray-600">NSFAS funding also accepted</p>
                  )}
                  <a
                    href={whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-yellow-400 px-4 py-3 font-semibold text-gray-900 transition hover:bg-yellow-500"
                  >
                    <MessageCircle className="h-5 w-5" />
                    Enquire on WhatsApp
                  </a>
                  <button
                    onClick={() => setGalleryIndex(0)}
                    className="mt-3 w-full rounded-xl border border-gray-300 px-4 py-3 font-semibold text-gray-900 transition hover:bg-gray-50"
                  >
                    View photos ({n})
                  </button>
                  <p className="mt-4 text-center text-xs text-gray-500">
                    You chat directly with the residence. No booking fees.
                  </p>
                </div>
              </aside>
            </div>
          </div>

          {/* Booking bar (mobile) */}
          <div className="fixed inset-x-0 bottom-0 flex items-center justify-between gap-3 border-t border-gray-200 bg-white px-4 py-3 lg:hidden">
            <p className="min-w-0 truncate text-sm text-gray-900">{priceLabel}</p>
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-yellow-400 px-5 py-3 text-sm font-semibold text-gray-900 transition hover:bg-yellow-500"
            >
              <MessageCircle className="h-4 w-4" />
              Enquire
            </a>
          </div>
        </div>
      )}

      {/* ======================= FULL-SCREEN GALLERY ===================== */}
      {galleryIndex !== null && (
        <div className="fixed inset-0 z-[60] flex flex-col bg-black" role="dialog" aria-modal="true" aria-label={`${title} photos`}>
          <div className="flex items-center justify-between px-4 py-3 text-white">
            <button
              onClick={() => setGalleryIndex(null)}
              className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium hover:bg-white/10"
            >
              <X className="h-5 w-5" />
              Close
            </button>
            <span className="text-sm">
              {galleryIndex + 1} / {n}
            </span>
            <span className="w-20" />
          </div>

          <div className="relative flex min-h-0 flex-1 items-center justify-center px-4" {...gallerySwipe}>
            <img
              key={galleryIndex}
              src={allImages[galleryIndex]}
              alt={`${title}, photo ${galleryIndex + 1}`}
              className="max-h-full max-w-full select-none rounded-lg object-contain"
            />
            {n > 1 && (
              <>
                <button
                  onClick={galleryPrev}
                  aria-label="Previous photo"
                  className="absolute left-4 hidden rounded-full bg-white/10 p-3 text-white transition hover:bg-white/20 sm:block"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  onClick={galleryNext}
                  aria-label="Next photo"
                  className="absolute right-4 hidden rounded-full bg-white/10 p-3 text-white transition hover:bg-white/20 sm:block"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </>
            )}
          </div>

          {n > 1 && (
            <div className="overflow-x-auto">
              <div className="mx-auto flex w-max gap-2 px-4 py-4">
                {allImages.map((src, i) => (
                  <button
                    key={i}
                    ref={(el) => (thumbRefs.current[i] = el)}
                    onClick={() => setGalleryIndex(i)}
                    aria-label={`Show photo ${i + 1}`}
                    className={`h-14 w-20 shrink-0 overflow-hidden rounded-lg ring-2 transition ${
                      i === galleryIndex
                        ? 'opacity-100 ring-yellow-400'
                        : 'opacity-50 ring-transparent hover:opacity-100'
                    }`}
                  >
                    <img src={src} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default AccommodationCard;
